import React, { useEffect, useState } from 'react'
import { useLanguage } from '../../../context/LanguageContext'

/**
 * 데스크톱 전용 사이드 스크롤 내비게이션.
 * 스크롤에 따라 현재 섹션을 골드로 표시하며 사이트 전체 흐름을 보여준다(몰입감).
 * 히어로를 벗어난 뒤 나타나고, 모바일에서는 숨긴다(반응형).
 */
const BASE_SECTIONS = [
    { id: 'intro', key: 'nav.story' },
    { id: 'menu-detail', key: 'nav.menu' },
    { id: 'gallery', key: 'nav.gallery' },
    { id: 'reviews', key: 'nav.reviews' },
]

const CONTACT_SECTION = { id: 'contact', key: 'nav.contact' }
const PRESS_SECTION = { id: 'press', key: 'nav.press' }

const SideNav: React.FC = () => {
    const { t, language } = useLanguage()
    const [activeId, setActiveId] = useState<string>('intro')
    const [visible, setVisible] = useState(false)

    // 언론보도는 한국어에서만 노출
    const SECTIONS = language === 'ko'
        ? [...BASE_SECTIONS, PRESS_SECTION, CONTACT_SECTION]
        : [...BASE_SECTIONS, CONTACT_SECTION]

    // 현재 보이는 섹션 추적
    useEffect(() => {
        const observers: IntersectionObserver[] = []
        const options: IntersectionObserverInit = { root: null, rootMargin: '-40% 0px -55% 0px', threshold: 0 }
        SECTIONS.forEach(({ id }) => {
            const el = document.getElementById(id)
            if (!el) return
            const observer = new IntersectionObserver(
                (entries) => entries.forEach((e) => { if (e.isIntersecting) setActiveId(id) }),
                options,
            )
            observer.observe(el)
            observers.push(observer)
        })
        return () => observers.forEach((o) => o.disconnect())
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [language])

    // 히어로를 지나면 노출
    useEffect(() => {
        const onScroll = () => setVisible(window.scrollY > window.innerHeight * 0.6)
        window.addEventListener('scroll', onScroll, { passive: true })
        onScroll()
        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    const goTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
    const activeIndex = Math.max(0, SECTIONS.findIndex((s) => s.id === activeId))

    return (
        <nav className={`restaurant-sidenav ${visible ? 'is-visible' : ''}`} aria-label="섹션 내비게이션">
            <span className="restaurant-sidenav-index">
                {String(activeIndex + 1).padStart(2, '0')}
                <span className="restaurant-sidenav-index-total"> / {String(SECTIONS.length).padStart(2, '0')}</span>
            </span>
            <ul className="restaurant-sidenav-list">
                {SECTIONS.map((s) => (
                    <li key={s.id}>
                        <button
                            type="button"
                            className={`restaurant-sidenav-item ${activeId === s.id ? 'is-active' : ''}`}
                            onClick={() => goTo(s.id)}
                        >
                            <span className="restaurant-sidenav-label">{t(s.key)}</span>
                            <span className="restaurant-sidenav-dot" />
                        </button>
                    </li>
                ))}
            </ul>
        </nav>
    )
}

export default SideNav
